import { ReactNode, useRef } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { Flex, FlexColumn } from '../Flex';
import { fontSize, fontWeight, space } from '../../style/utils';

const StepsWrapper = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.palette.border.subtle};
`;

const Step = styled(Flex)<{ $selected?: boolean; $disabled?: boolean }>`
  position: relative;
  width: 100%;
  gap: ${space('xxl')};
  padding: ${space(0, 'xxl')};
  height: 72px;
  background: ${({ theme, $selected }) =>
    $selected
      ? theme.tokens.components.wizard.selectedBackground
      : theme.palette.surface.base};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.fontWeightBold : fontWeight('regular')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  :hover {
    background: ${({ theme, $disabled, $selected }) =>
      !$disabled &&
      ($selected
        ? theme.tokens.components.wizard.selectedHoverBackground
        : theme.tokens.components.wizard.hoverBackground)};
  }
`;

const StepLeft = styled(Flex)`
  gap: ${space('xxl')};
  align-items: center;
`;

const StepText = styled(Typography)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-weight: inherit;
  white-space: nowrap;
  color: ${({ theme, $selected, $stepOver }) =>
    $selected
      ? theme.palette.accent.secondary
      : $stepOver
      ? theme.palette.content.primary
      : theme.palette.content.subtle};
`;

const StepSubText = styled(Typography)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.muted};
`;

const StepDetail = styled(Flex)`
  gap: ${space('sm')};
  align-items: center;
`;

const StepIndex = styled(StepText)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-size: ${fontSize('xxxl')};
  color: ${({ theme, $selected, $stepOver }) =>
    $selected
      ? theme.palette.accent.secondary
      : $stepOver
      ? theme.tokens.components.wizard.doneIndex
      : theme.palette.content.subtle};
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.surface.hover};
  height: 100%;
  width: 1px;
  min-width: 1px;
`;

type StepBoxProps = {
  index: number;
  currentStep: number;
  title: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick: () => void;
};

const StepBox = ({
  index,
  currentStep,
  title,
  description,
  icon,
  disabled,
  onClick,
}: StepBoxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const stepNumber = index + 1; // Convert to 1-indexed for display

  return (
    <Step
      ref={ref}
      $selected={index === currentStep}
      $disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <StepLeft>
          <StepIndex
            $selected={index === currentStep}
            $stepOver={index < currentStep}
          >
            {stepNumber}
          </StepIndex>
          <FlexColumn>
            <StepText
              $selected={index === currentStep}
              $stepOver={index < currentStep}
            >
              {title}
            </StepText>
            {description && (
              <StepDetail>
                {icon && icon}
                <StepSubText noWrap>{description}</StepSubText>
              </StepDetail>
            )}
          </FlexColumn>
        </StepLeft>
      </Flex>
    </Step>
  );
};

type StepInfo = {
  title: string;
  description?: string;
  icon?: ReactNode;
};

type Props = {
  steps: StepInfo[];
  currentStep: number;
  onStepClick: (index: number) => void;
};

const WizardStepper = ({ steps, currentStep, onStepClick }: Props) => (
  <StepsWrapper divider={<Divider />}>
    {steps.map((step, index) => (
      <StepBox
        key={`wizard-step-${step.title}`}
        index={index}
        currentStep={currentStep}
        title={step.title}
        description={step.description}
        icon={step.icon}
        disabled={index > currentStep}
        onClick={() => onStepClick(index)}
      />
    ))}
  </StepsWrapper>
);

export default WizardStepper;
